// LAP-548 / LAP-550 — Modale « Ajouter au suivi » depuis la fiche détail d'une
// valeur. Pré-remplit le prix courant à partir de l'actif, laisse choisir le
// type d'investissement + le prix cible, puis crée une opportunité suivie via
// /api/investment/tracked-opportunitie-operations/ (workflow 2.2 de la doc FM005).
import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { makeGetRequest, makePostRequest } from 'utils/api'
import { assetName, assetPrice, describeError } from './AddToPortfolioDialog'

const cap = (v, n) => String(v == null ? '' : v).slice(0, n)
const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

export default function AddToTrackedDialog({ open, asset, onClose, onAdded }) {
  const { t } = useTranslation('layout')
  const [types, setTypes] = useState([])
  const [typeId, setTypeId] = useState('')
  const [price, setPrice] = useState('0')
  const [targetPrice, setTargetPrice] = useState('0')
  const [targetValue, setTargetValue] = useState('0')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open || !asset) return
    const p = assetPrice(asset)
    setPrice(String(p))
    // Cible par défaut : +10 % du prix courant (l'utilisateur peut l'ajuster).
    const target = p ? +(p * 1.1).toFixed(2) : 0
    setTargetPrice(String(target))
    setTargetValue(String(target))
    const fetchTypes = async () => {
      const { data, status } = await makeGetRequest(
        '/api/investment/investment-type/',
        {},
        true,
      )
      if (status === 200 && Array.isArray(data)) {
        setTypes(data)
        setTypeId((prev) => prev || (data[0] && data[0].id) || '')
      }
    }
    fetchTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asset])

  // +/- valeur attendue à l'atteinte de la cible (cf. table TrackedOpportunitie).
  const valueChange = +(num(targetValue) - num(targetPrice)).toFixed(2)
  const valueChangePct = num(targetPrice)
    ? +(((num(targetValue) - num(targetPrice)) / num(targetPrice)) * 100).toFixed(2)
    : 0

  const handleConfirm = async () => {
    if (!typeId) {
      toast.error(
        t('investments.detail.trackDialog.typeRequired', 'Choisissez un type d’investissement'),
      )
      return
    }
    setSubmitting(true)
    const fd = new FormData()
    fd.append('type_id', typeId)
    fd.append('name', cap(assetName(asset), 255))
    fd.append('description', cap(asset?.sector || asset?.type || '', 480))
    fd.append('sector', cap(asset?.sector || asset?.type || '', 255))
    fd.append('industry', cap(asset?.industry || '', 255))
    fd.append('price', num(price))
    fd.append('target_price', num(targetPrice))
    fd.append('target_value', num(targetValue))
    fd.append('value_change', valueChange)
    fd.append('value_change_pct', valueChangePct)
    const res = await makePostRequest(
      '/api/investment/tracked-opportunitie-operations/',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
      true,
    )
    setSubmitting(false)
    if (res && res.status === 201) {
      toast.success(
        t('investments.detail.trackDialog.added', 'Valeur ajoutée à votre suivi d’opportunités'),
      )
      if (onAdded) onAdded(res.data)
      onClose()
    } else {
      toast.error(
        `${t('investments.detail.trackDialog.failed', 'Échec de l’ajout au suivi')} — ${describeError(res)}`,
      )
    }
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={submitting ? undefined : onClose}>
      <DialogTitle sx={{ color: 'black' }}>
        {t('investments.detail.trackDialog.title', 'Ajouter au suivi')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', mb: 2 }}>
          {t(
            'investments.detail.trackDialog.intro',
            'Définissez un prix cible pour suivre cette valeur dans vos opportunités.',
          )}
        </Typography>

        {asset && (
          <Typography sx={{ fontWeight: 600, color: 'black', mb: 2 }}>
            {assetName(asset)}
            {asset.symbol ? ` · ${asset.symbol}` : ''}
          </Typography>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="add-track-type-label">
            {t('investments.detail.trackDialog.type', 'Type d’investissement')}
          </InputLabel>
          <Select
            labelId="add-track-type-label"
            label={t('investments.detail.trackDialog.type', 'Type d’investissement')}
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
          >
            {types.map((ty) => (
              <MenuItem key={ty.id} value={ty.id}>
                {ty.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            type="number"
            size="small"
            label={t('investments.detail.trackDialog.price', 'Prix actuel')}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputProps={{ step: 'any' }}
            sx={{ flex: 1, minWidth: 140 }}
          />
          <TextField
            type="number"
            size="small"
            label={t('investments.detail.trackDialog.targetPrice', 'Prix cible')}
            value={targetPrice}
            onChange={(e) => setTargetPrice(e.target.value)}
            inputProps={{ step: 'any' }}
            sx={{ flex: 1, minWidth: 140 }}
          />
          <TextField
            type="number"
            size="small"
            label={t('investments.detail.trackDialog.targetValue', 'Valeur cible')}
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
            inputProps={{ step: 'any' }}
            sx={{ flex: 1, minWidth: 140 }}
          />
        </Box>

        <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', mt: 1.5 }}>
          {t('investments.detail.trackDialog.expectedChange', '+/- valeur estimée')} :{' '}
          <strong style={{ color: valueChange >= 0 ? '#1a7f37' : '#c4314b' }}>
            {valueChange.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} (
            {valueChangePct.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %)
          </strong>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('investments.detail.trackDialog.cancel', 'Annuler')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || !asset}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t('investments.detail.trackDialog.confirm', 'Suivre')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
